import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener, Grow, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { fetchSkills } from '../../../api/skill';
import { COLORS } from '../../../constants/colors';
import { useDebounce, useNotification } from '../../../hooks';
import Input from '../../common/input';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    '& > :last-child': {
      borderBottomRightRadius: '.3125rem',
      borderBottomLeftRadius: '.3125rem',
    },
  },
  input: {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  resultsWrapper: {
    position: 'absolute',
    zIndex: 100,
    bottom: 0,
    transform: 'translateY(100%) !important',
    width: '100%',
    backgroundColor: COLORS.grayScale[5],
    color: COLORS.grayScale[4],
    margin: 0,
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '20rem',
    overflow: 'auto',
    padding: '0.25rem 0',
  },
  result: {
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: '.025rem 1.25rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.primary.main,
      color: COLORS.grayScale[5],
    },
  },
  disabled:{
    '& input:disabled': {
      background: '#a7a7a7',
    },
    '& > :first-child': {
      '& > :first-child': {
        background: '#a7a7a7',
        marginLeft: '0 !important',
      },
    },
  }
});

const Searcher = ({ onSelect, disabled }) => {
  const classes = useStyles();
  const notifications = useNotification();

  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchSkillsDebounced = useDebounce(
    (name) => {
      return name.length > 1
        ? fetchSkills(name)
            .ready()
            .then((res) => {
              if (res.success) setResults(res.data);
              else
                notifications.error({
                  message:
                    'An error has occured while searching skills. Please, try again later',
                });
            })
        : null;
    },

    1000
  );

  useEffect(() => {
    fetchSkillsDebounced(search);
  }, [search]);

  useEffect(() => {
    // If the input search is empty then we clear the results
    if (search.length === 0) setResults([]);
  }, [search]);

  useEffect(() => {
    // When an item is selected we forward it and clear the selection
    if (selected) {
      onSelect(selected);
      setSelected(null);
    }
  }, [selected]);

  useEffect(() => {
    // When an item is selected we clear the input search
    if (selected) setSearch('');
  }, [selected]);

  useEffect(() => {
    // Show/Hide the results depend on its length
    setOpen(results.length > 0);
  }, [results]);

  const quote = function(str) {
    return str.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={disabled
            ? [classes.container, classes.disabled].join(' ')
            : classes.container}>
        <Input
          className={open && results.length ? classes.input : null}
          value={search}
          startIcon={<FontAwesomeIcon icon={faSearch} />}
          placeholder="Search skill"
          onChange={({ target }) => setSearch(target.value)}
          onFocus={() => setOpen(true)}
          disabled={disabled}
        />
        {open && (
          <Grow in={results.length > 0}>
            <div className={classes.resultsWrapper}>
              <div className={classes.results}>
                {results.map((skill, index) => (
                  <span
                    key={`skill-${index}`}
                    onClick={() => setSelected(skill)}
                    className={classes.result}
                    dangerouslySetInnerHTML={{
                      __html: skill.name.replace(
                        new RegExp(`${quote(search)}`, 'gi'),
                        (match) => `<b>${match}</b>`
                      ),
                    }}
                  ></span>
                ))}
              </div>
            </div>
          </Grow>
        )}
      </div>
    </ClickAwayListener>
  );
};

Searcher.propTypes = {
  onSelect: PropTypes.func.isRequired,
};

export default Searcher;
